import Facebook from '@clearkit/icons/glyphs/Facebook';
import Github from '@clearkit/icons/glyphs/Github';
import GoIcon from '@clearkit/icons/glyphs/Go';
import Linkedin from '@clearkit/icons/glyphs/Linkedin';
import Twitter from '@clearkit/icons/glyphs/Twitter';
import { CKCompanyLogo } from '@clearkit/react';
import startCase from 'lodash/startCase';
import { Link } from 'react-router-dom';

import paths from '~/lib/paths';
const truncateLength = 120;

const PersonDetails = ({ person }) => {
  const company = person.company || person.corporateCompany;

  const [showingDescription, setShowingDescription] = useState(
    company?.description?.length < truncateLength,
  );

  return (
    <div className="relative flex flex-col justify-between flex-shrink-0 w-full text-sm border-b border-gray-200">
      <div className="flex flex-row">
        <div className="flex-grow px-10 pb-4 pr-4 text-base">
          <div className="py-6 mb-6 border-b border-gray-200">
            <div className="flex flex-col flex-shrink-0 pr-8 mb-4">
              <span className="mb-1 text-xs text-gray-500">Name</span>
              <span className="flex flex-row font-medium text-gray-900">
                {person.name}
                <span className="flex flex-row ml-auto space-x-3">
                  {person.twitter && (
                    <a
                      className="text-twitter"
                      rel="noopener noreferrer"
                      target="_blank"
                      href={person.twitter}>
                      <Twitter />
                    </a>
                  )}
                  {person.facebook && (
                    <a
                      className="text-facebook"
                      rel="noopener noreferrer"
                      target="_blank"
                      href={person.facebook}>
                      <Facebook />
                    </a>
                  )}
                  {person.github && (
                    <a
                      className="text-github"
                      rel="noopener noreferrer"
                      target="_blank"
                      href={person.github}>
                      <Github />
                    </a>
                  )}
                  {person.linkedin && (
                    <a
                      className="text-linkedin"
                      rel="noopener noreferrer"
                      target="_blank"
                      href={person.linkedin}>
                      <Linkedin />
                    </a>
                  )}
                </span>
              </span>
            </div>

            <div className="flex flex-col flex-shrink-0 pr-8">
              <span className="mb-1 text-xs text-gray-500">Email</span>
              {person.email ? (
                <a
                  href={`mailto://${person.email}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue">
                  {person.email}
                </a>
              ) : (
                <span>&mdash;</span>
              )}
            </div>
          </div>

          <div>
            <div className="flex flex-col flex-shrink-0 pr-8 mb-4">
              <span className="mb-1 text-xs text-gray-500">Employment</span>
              <span className="font-medium text-gray-900">
                {person.employment || <>&mdash;</>}
              </span>
            </div>

            <div className="flex flex-row items-center mt-4">
              <div className="flex flex-col pr-4 min-w-24">
                <span className="mb-1 text-xs text-gray-500">Role</span>
                <span>{startCase(person.role) || <>&mdash;</>}</span>
              </div>
              <div className="flex flex-col pr-4 min-w-24">
                <span className="mb-1 text-xs text-gray-500">Seniority</span>
                <span>{startCase(person.seniority) || <>&mdash;</>}</span>
              </div>
              <div className="flex flex-col pr-4 min-w-24">
                <span className="mb-1 text-xs text-gray-500">Location</span>
                <span>{person.location || <>&mdash;</>}</span>
              </div>
            </div>
          </div>
        </div>

        {company && (
          <div className="z-10 p-4 pr-10 -mb-12 w-xxs">
            <div className="p-4 my-4 ck-box ck-box--card">
              <div className="flex flex-row items-center pb-4 pl-2 border-b border-gray-200">
                <Link to={paths.company(company.id)}>
                  <CKCompanyLogo
                    domain={company.domain}
                    size={56}
                    cornerRadius={8}
                    className="mr-4 shadow-sm"
                  />
                </Link>

                <div className="flex flex-col text-sm leading-snug">
                  <span className="text-lg font-medium text-gray-900">
                    {company.name}
                  </span>
                  <a
                    target="blank"
                    href={`https://${company.domain}`}
                    rel="noopener noreferrer">
                    {company.domain}
                  </a>
                  {company.sector && <span>{company.sector}</span>}
                </div>
                <Link
                  to={paths.company(company.id)}
                  className="self-start mt-1 ml-auto mr-1 align-end">
                  <GoIcon />
                </Link>
              </div>

              {(company.employeesRange || company.estimatedAnnualRevenue) && (
                <div className="flex flex-shrink-0 px-2 py-4 text-sm border-b border-gray-200 hide-if-empty">
                  <div className="flex flex-col flex-shrink-0 mr-8">
                    <span className="mb-1 text-xs text-gray-500">
                      Employees
                    </span>
                    <span>{company.employeesRange || <>&mdash;</>}</span>
                  </div>
                  <div className="flex flex-col flex-shrink-0">
                    <span className="mb-1 text-xs text-gray-500 truncate">
                      Est. Annual Revenue
                    </span>
                    <span>
                      {company.estimatedAnnualRevenue || <>&mdash;</>}
                    </span>
                  </div>
                </div>
              )}

              {company.description && (
                <div className="px-2 pt-4">
                  <p className="leading-snug">
                    {showingDescription
                      ? company?.description
                      : company?.description?.substring(0, truncateLength) +
                        '…'}
                  </p>

                  {!showingDescription && (
                    <p>
                      <a
                        href="#none"
                        className="mt-2 text-xs text-blue-400"
                        onClick={() =>
                          setShowingDescription(!showingDescription)
                        }>
                        Show full description
                      </a>
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="w-full">
        <div className="px-10 py-4 border-t border-gray-200 bg-gray-0">
          <div className="w-7/12">
            <div className="flex flex-col flex-shrink-0 py-2 pr-8">
              <span className="mb-1 text-xs text-gray-500">Bio</span>
              <span>{person.bio || <>&mdash;</>}</span>
            </div>

            <div className="flex flex-col flex-shrink-0 py-2 pr-8">
              <span className="mb-1 text-xs text-gray-500">Website</span>
              <a
                rel="noopener noreferrer"
                target="_blank"
                href={company.domain}>
                {person.company.domain || <>&mdash;</>}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

PersonDetails.propTypes = {
  person: PropTypes.shape({
    twitter: PropTypes.string,
    facebook: PropTypes.string,
    github: PropTypes.string,
    linkedin: PropTypes.string,
    bio: PropTypes.string,
    company: PropTypes.shape({
      name: PropTypes.string,
      domain: PropTypes.string,
      sector: PropTypes.string,
      employeesRange: PropTypes.string,
      estimatedAnnualRevenue: PropTypes.string,
      description: PropTypes.string,
    }),
    corporateCompany: PropTypes.shape({
      name: PropTypes.string,
      domain: PropTypes.string,
      sector: PropTypes.string,
      employeesRange: PropTypes.string,
      estimatedAnnualRevenue: PropTypes.string,
      description: PropTypes.string,
    }),
    email: PropTypes.string,
    employment: PropTypes.string,
    location: PropTypes.string,
    name: PropTypes.string,
    role: PropTypes.string,
    seniority: PropTypes.string,
  }),
};

export default PersonDetails;
